import React, { ReactNode, useMemo, useState } from "react"
import { uniqueId } from "lodash"
import classNames from "classnames"
import { useGssSuppression } from "@/utils/hooks/useSuppressions"
import Loader from "@components/Loader/Loader"
import { MatrixContent, useContentJsx } from "@/utils/hooks/useContent"
import BrowserWarning from "../BrowserWarning/BrowserWarning"
import TaxFileNumberMissing from "./dashboardNotifications/TaxFileNumberMissing"
import MobileNumberMissing from "./dashboardNotifications/MobileNumberMissing"
import SalaryContinuanceIncrease from "./dashboardNotifications/SalaryContinuanceIncrease"
import GssVarianceNotice from "./dashboardNotifications/GssVarianceNotice"
import EmailEndingNotice from "./dashboardNotifications/EmailEndingNotice"
import NotificationIcon from "./NotificationIcons"
import ProtectYourSuperWidget from "@components/ProtectYourSuperWidget/ProtectYourSuperWidget"
import IlbaHomepageBanners from "../IlbaHomepageBanners/IlbaHomepageBanners"

type NotificationProps = {
  notificationType:
    | "alert"
    | "help"
    | "success"
    | "bell"
    | "bell-alert"
    | "info"
    | "info-text"
    | "mail"
    | "file"
    | "loader"
  heading?: ReactNode
  /** Semantic heading level for `heading` — defaults to h4 for existing notifications */
  headingLevel?: 2 | 3 | 4
  buttonPullout?: ReactNode
  closeButton?: boolean
  inline?: boolean
  children: ReactNode
  className?: string | null
  onClose?: () => void
}

function notificationRegionLabel(
  notificationType: NotificationProps["notificationType"]
): string {
  switch (notificationType) {
    case "alert":
      return "Alert"
    case "help":
      return "Help"
    case "success":
      return "Success"
    case "bell":
    case "bell-alert":
      return "Notification"
    case "info":
    case "info-text":
      return "Information"
    case "mail":
      return "Mail"
    case "file":
      return "Document"
    case "loader":
      return "Loading"
    default:
      return "Notice"
  }
}

function Notification({
  buttonPullout,
  children = null,
  heading,
  headingLevel = 4,
  notificationType,
  closeButton,
  className = null,
  inline = false,
  onClose,
}: NotificationProps) {
  const [showNotice, setShowNotice] = useState(true)
  const headingId = useMemo(() => `notification-heading-${uniqueId()}`, [])
  const HeadingTag = `h${headingLevel}` as keyof JSX.IntrinsicElements
  if (!showNotice) {
    return null
  }

  return (
    <div
      className={classNames(
        "notification",
        className,
        notificationType ? `notification--${notificationType}` : null,
        inline ? `notification--inline` : null
      )}
      role="region"
      aria-labelledby={heading ? headingId : undefined}
      aria-label={
        heading ? undefined : notificationRegionLabel(notificationType)
      }
    >
      {notificationType && (
        <div
          className="notification--icon"
          aria-hidden={notificationType === "loader" ? undefined : true}
        >
          <NotificationIcon type={notificationType} />
        </div>
      )}

      <div className="notification--container">
        {heading && <HeadingTag id={headingId}>{heading}</HeadingTag>}
        <div className="notification-content">
          <div className="notification-content__textcol">{children}</div>
          {buttonPullout && (
            <div className="notification-content__btncol">{buttonPullout}</div>
          )}
        </div>
        {closeButton && (
          <button
            className="notification__close"
            onClick={() => {
              setShowNotice(false)
              onClose?.()
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  )
}

function GSSSuppressionNotice() {
  const { isFetching, hasSuppression, hasMultipleAccounts } =
    useGssSuppression()

  const { contentBody: prohibitMultiple } = useContentJsx(
    "dashboard.prohibit.your_gss_benefit_is_currently_not_available"
  ) as MatrixContent
  const { contentBody: prohibitSingle } = useContentJsx(
    "dashboard.prohibit.prohibit_for_one_account_only"
  ) as MatrixContent

  if (isFetching) {
    return <Loader />
  }

  if (hasSuppression) {
    return (
      <Notification notificationType={"alert"}>
        {hasMultipleAccounts ? prohibitMultiple : prohibitSingle}
      </Notification>
    )
  }

  return null
}

// Global Notifications
export function GlobalNotifications() {
  return (
    <div className="global-notifications">
      <BrowserWarning />
      <GSSSuppressionNotice />
    </div>
  )
}

// Dashboard Notifications
export function InPageNotification() {
  const hasNotification = true
  if (hasNotification) {
    return (
      <div className="dashboard-notifications">
        <TaxFileNumberMissing />
        <SalaryContinuanceIncrease />
        <MobileNumberMissing />
        <GssVarianceNotice />
        <EmailEndingNotice />
        <ProtectYourSuperWidget />
        <IlbaHomepageBanners />
      </div>
    )
  }

  return null
}

type StatusNotificationProps = {
  statusText: string | ReactNode
  children?: ReactNode
}

export function StatusNotification({
  statusText,
  children,
}: StatusNotificationProps) {
  return (
    <div className="change-notification__status-col">
      <div>Status:</div>
      <div className="change-notification__status-text">{statusText}</div>
      {children}
    </div>
  )
}

export default Notification
